"use client";

import { useState, type FormEvent } from "react";
import type { FieldErrors, FormMessage } from "@/components/forms/types";
import { submitForm, type FormId } from "@/lib/forms";

/**
 * What signin-form.js, register-form.js and forgot-password-form.js shared:
 * send the answers, put the reply in the form's message box, and on success
 * take the form away and leave the message.
 *
 * The theme's state classes (is-loading while sending, then show-message with
 * success or error) are data attributes here, which the forms spread onto
 * themselves and style their parts from: data-loading, data-message and
 * data-status.
 *
 * A result can also carry a redirect (the app, after signing in), which the
 * form follows a second later, as the theme did.
 */
export function useAuthForm(
  id: FormId,
  collect: (form: HTMLFormElement) => Record<string, string>,
) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<FormMessage | null>(null);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const data = collect(event.currentTarget);

    setMessage(null);
    setStatus(null);
    setErrors({});
    setLoading(true);

    let result;

    try {
      result = await submitForm(id, data);
    } catch {
      result = null;
    }

    setLoading(false);

    // The theme alert()ed here; the message box says it instead.
    if (!result) {
      setStatus("error");
      setMessage({
        header: "Server error.",
        body: "Please try again later or contact the administrator.",
      });

      return;
    }

    setMessage({ body: result.message });
    setStatus(result.ok ? "success" : "error");
    setErrors(result.ok ? {} : (result.errors ?? {}));

    // Signing in sends the member on to the app once they've seen the message.
    const redirect = result.ok ? result.redirect : undefined;

    if (redirect) {
      window.setTimeout(() => {
        if (redirect === "self") {
          window.location.reload();
        } else {
          window.location.href = redirect;
        }
      }, 1000);
    }
  };

  /** The form's state, as the data attributes its parts style themselves from. */
  const stateAttributes = {
    "data-loading": loading,
    "data-message": message ? "" : undefined,
    "data-status": status ?? undefined,
  };

  return {
    loading,
    message,
    success: status === "success",
    errors,
    onSubmit,
    stateAttributes,
  };
}

/** A named control's value, as $form.find('input[name=...]').val() read it. */
export function fieldValue(form: HTMLFormElement, name: string) {
  const element = form.elements.namedItem(name);

  if (element instanceof RadioNodeList) {
    return element.value;
  }

  return element && "value" in element ? String(element.value) : "";
}
